/****************************************************************************************
 *  launchdarkly.service.ts                                                             *
 *  ----------------------------------------------------------------------------------  *
 *  Flipt Feature Flag Service Wrapper (100% backward-compatible with WebApp callers). *
 *  • Initialized with Flipt Web Provider via OpenFeature.                             *
 *  • Real-time updates pushed via RxJS BehaviorSubjects.                               *
 ****************************************************************************************/

import { Injectable, NgZone } from '@angular/core';
import { OpenFeature, Client as FliptClient, EvaluationContext } from '@openfeature/web-sdk';
import { FliptWebProvider } from '@openfeature/flipt-web-provider';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '@env';
import { LoggingService, LogSeverity } from '@core/services/logging.service';
import { Campaign, CAMPAIGN_ID } from '@core/models';
import { LocationService } from '@shared/services/location.service';
import { StoreService } from '@core/services/store.service';
import { Moment } from 'moment';
import { MenuItemAlert } from '@core/models/menu-item-alert.model';

export type LDThemeDaypart = {
  code: string;
  content: LDTheme;
  id: string;
};

export type LDThemeCampaign = {
  code: string;
  id: string;
  dayparts: LDThemeDaypart[];
  priority: number;
};

export type LDTheme = {
  audioTriggers?: LDThemeAudioTrigger[];
  class?: string;
  speechBubbleStyle?: string;
  startConversationTrigger?: boolean;
};

export type LDThemeAudioTrigger = {
  id: string;
  phrases: string[];
};

interface FlagStream<T> {
  key: string;
  subject: BehaviorSubject<T>;
  fallback: T;
  logLevel: LogSeverity;
}

interface EvaluationMetricKey {
  flagKey: string;
  namespace: string;
  environment: string;
  flagType: 'boolean' | 'variant';
  match: boolean;
  reason: string;
  segmentKeys: string[];
  value: string;
}

interface EvaluationMetricItem extends EvaluationMetricKey {
  count: number;
}

@Injectable({ providedIn: 'root' })
export class LaunchDarklyService {
  public fliptClient?: FliptClient;
  public fliptProvider?: FliptWebProvider;
  private fliptInitPromise?: Promise<void>;
  private currentSiteNumber = 'unknown';
  private static isLivePollerActive = false;
  private static isMetricsFlusherActive = false;
  private lastSnapshotDigest = '';
  private lastSnapshotMaxTimestamp = 0;
  private lastEtag: string | null = null;
  private readonly metricsBuffer = new Map<string, EvaluationMetricItem>();

  private readonly fliptUrl = environment.flipt.url;
  private readonly fliptNamespace = environment.flipt.namespace;
  private readonly fliptEnvironment = environment.flipt.environment || 'default';
  private readonly flagStreams = new Map<string, FlagStream<any>>();

  constructor(
    private logger: LoggingService,
    private zone: NgZone,
    private locationService: LocationService,
    private storeService: StoreService,
  ) {
    this.currentSiteNumber = this.resolveInitialStoreId();
    this.fliptInitPromise = this.initializeFlipt(this.currentSiteNumber);

    // Watch for dynamic store ID changes from URL / store service
    this.storeService.storeId$.subscribe((newId) => {
      if (newId && newId !== this.currentSiteNumber) {
        this.updateStoreContext(newId);
      }
    });
  }

  private resolveInitialStoreId(): string {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlStoreId = urlParams.get('storeId') || urlParams.get('siteNum');
      if (urlStoreId) {
        localStorage.setItem('storeId', urlStoreId);
        return urlStoreId;
      }
    } catch {
      // Ignore URL parsing errors
    }
    return localStorage.getItem('storeId') || 'unknown';
  }

  public async updateStoreContext(siteNumber: string): Promise<void> {
    this.currentSiteNumber = siteNumber;
    const fliptContext: EvaluationContext = {
      targetingKey: `store-${siteNumber}`,
      siteNumber,
    };
    OpenFeature.setContext(fliptContext);
    if (this.fliptProvider) {
      try {
        await (this.fliptProvider as any)?._client?.refresh();
      } catch {}
    }
    this.zone.run(() => {
      this.refreshAllStreams();
    });
  }

  /* ----------------------------------------------------------------------- */
  /*  Flipt Provider Initialization                                          */
  /* ----------------------------------------------------------------------- */
  private async initializeFlipt(siteNumber: string): Promise<void> {
    try {
      const fliptContext: EvaluationContext = {
        targetingKey: `store-${siteNumber}`,
        siteNumber,
      };

      this.fliptProvider = new FliptWebProvider(this.fliptNamespace, {
        url: this.fliptUrl,
      });

      OpenFeature.setContext(fliptContext);
      await OpenFeature.setProviderAndWait(this.fliptProvider);

      this.fliptClient = OpenFeature.getClient();
      this.logger.writeLog(`[Flipt] Web Provider successfully initialized and authoritative at ${this.fliptUrl}`, {
        severity: LogSeverity.INFO,
      });

      // Capture initial snapshot checksum and ETag to prevent spurious change detection on first poll
      const snapshotUrl = `${this.fliptUrl}/client/v2/environments/${this.fliptEnvironment}/namespaces/${this.fliptNamespace}/snapshot`;
      try {
        const initRes = await fetch(snapshotUrl);
        if (initRes.ok) {
          this.lastEtag = initRes.headers.get('etag');
          const initData = await initRes.json();
          this.lastSnapshotDigest = initData.digest || '';
          this.lastSnapshotMaxTimestamp = Array.isArray(initData.flags)
            ? Math.max(0, ...initData.flags.map((f: any) => (f.updatedAt ? new Date(f.updatedAt).getTime() : 0)))
            : 0;
        }
      } catch {}

      // Initial stream priming
      this.zone.run(() => {
        this.refreshAllStreams();
      });

      // Poll for live dashboard changes every 60s (single instance only)
      if (!LaunchDarklyService.isLivePollerActive) {
        LaunchDarklyService.isLivePollerActive = true;
        this.zone.runOutsideAngular(() => {
          setInterval(async () => {
            await this.pollFliptLiveUpdates();
          }, 60000);
        });
      }

      // Flush evaluation metrics to Flipt Metrics Bridge every 30s + on page unload
      if (!LaunchDarklyService.isMetricsFlusherActive) {
        LaunchDarklyService.isMetricsFlusherActive = true;
        this.zone.runOutsideAngular(() => {
          setInterval(() => {
            this.flushEvaluationMetrics();
          }, 30000);
          if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => this.flushEvaluationMetrics());
            document.addEventListener('visibilitychange', () => {
              if (document.visibilityState === 'hidden') {
                this.flushEvaluationMetrics();
              }
            });
          }
        });
      }
    } catch (error) {
      this.logger.writeLog(`[Flipt] Failed to initialize Flipt Web Provider at ${this.fliptUrl}: ${error}`, {
        severity: LogSeverity.ERROR,
      });
      this.fliptClient = undefined;
      this.fliptProvider = undefined;
    }
  }

  /**
   * Polls the Flipt snapshot endpoint for real-time dashboard flag updates.
   *
   * Network & Efficiency Optimizations:
   * 1. Sends `If-None-Match: <lastEtag>` header so the server responds with `HTTP 304 Not Modified`
   *    and 0 bytes transferred when flags are unchanged.
   * 2. Enforces monotonic timestamping (`maxTs >= lastSnapshotMaxTimestamp`) and digest verification
   *    to reject stale reads from lagging Kubernetes cluster replica pods.
   */
  private async pollFliptLiveUpdates(): Promise<void> {
    try {
      const rawClient = (this.fliptProvider as any)?._client;
      if (rawClient?.engine) {
        const snapshotUrl = `${this.fliptUrl}/client/v2/environments/${this.fliptEnvironment}/namespaces/${this.fliptNamespace}/snapshot`;
        const headers: HeadersInit = this.lastEtag ? { 'If-None-Match': this.lastEtag } : {};
        const res = await fetch(snapshotUrl, { headers });

        // 304 Not Modified: 0 bytes transferred, no JSON parsing required
        if (res.status === 304) {
          return;
        }

        if (res.ok) {
          this.lastEtag = res.headers.get('etag');
          const data = await res.json();
          const digest = data.digest || '';
          const maxTs = Array.isArray(data.flags)
            ? Math.max(0, ...data.flags.map((f: any) => (f.updatedAt ? new Date(f.updatedAt).getTime() : 0)))
            : 0;

          // 1. If digest is identical, no change has occurred.
          if (this.lastSnapshotDigest && this.lastSnapshotDigest === digest) {
            return;
          }

          // 2. Reject stale reads from lagging replica pods in multi-instance clusters
          if (this.lastSnapshotMaxTimestamp > 0 && maxTs < this.lastSnapshotMaxTimestamp) {
            this.logger.writeLog('[Flipt-Live] Ignored stale snapshot from lagging cluster replica', {
              replicaTimestamp: maxTs,
              currentTimestamp: this.lastSnapshotMaxTimestamp,
              severity: LogSeverity.DEBUG,
            });
            return;
          }

          this.lastSnapshotDigest = digest;
          if (maxTs > this.lastSnapshotMaxTimestamp) {
            this.lastSnapshotMaxTimestamp = maxTs;
          }

          // Update in-memory WebAssembly evaluation engine
          rawClient.engine.snapshot(data);
          this.logger.writeLog('[Flipt-Live] Flag changes detected from Flipt dashboard!', {
            severity: LogSeverity.INFO,
          });
          this.zone.run(() => {
            this.refreshAllStreams();
          });
        }
      }
    } catch {
      // Ignore background network hiccups
    }
  }

  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) return false;
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k, i) => k === keysB[i] && this.deepEqual(a[k], b[k]));
  }

  private async refreshAllStreams(): Promise<void> {
    const streamKeys = Array.from(this.flagStreams.keys());
    const evaluations = await Promise.all(
      streamKeys.map(async (key) => {
        const stream = this.flagStreams.get(key)!;
        const val = await this.variation(stream.key, stream.fallback, stream.logLevel);
        return { stream, val };
      }),
    );

    // Only emit to subscribers IF the value actually changed!
    this.zone.run(() => {
      evaluations.forEach(({ stream, val }) => {
        if (!this.deepEqual(stream.subject.getValue(), val)) {
          this.logger.writeLog(`[Flipt] Flag '${stream.key}' value updated dynamically`, {
            flagKey: stream.key,
            newVal: val,
            severity: stream.logLevel,
          });
          stream.subject.next(val);
        }
      });
    });
  }

  /* =======================================================================
   *  GENERIC FLIPT EVALUATION HELPER
   * ===================================================================== */

  private async variation<T>(flagKey: string, fallback: T, level: LogSeverity): Promise<T> {
    if (this.fliptInitPromise) {
      await this.fliptInitPromise;
    }

    if (!this.fliptClient) {
      return fallback;
    }

    try {
      let value: T;
      let reason = 'DEFAULT_EVALUATION_REASON';
      let match = false;
      let segmentKeys: string[] = [];
      let variantValueStr = '';

      if (typeof fallback === 'boolean') {
        const rawClient = (this.fliptProvider as any)?._client;
        const siteNumber = this.currentSiteNumber || localStorage.getItem('storeId') || 'unknown';
        if (rawClient?.evaluateBoolean) {
          const evalRes = rawClient.evaluateBoolean({
            flagKey,
            entityId: `store-${siteNumber}`,
            context: { siteNumber },
          });
          value = (evalRes?.enabled ?? fallback) as unknown as T;
          reason = evalRes?.reason || (evalRes?.enabled ? 'MATCH_EVALUATION_REASON' : 'DEFAULT_EVALUATION_REASON');
          match = Boolean(evalRes?.enabled);
        } else {
          value = (await this.fliptClient.getBooleanValue(flagKey, fallback as unknown as boolean)) as unknown as T;
          match = Boolean(value);
        }
        variantValueStr = String(value);

        this.recordEvaluationMetric({
          flagKey,
          namespace: this.fliptNamespace,
          environment: 'default',
          flagType: 'boolean',
          match,
          reason,
          segmentKeys,
          value: variantValueStr,
        });
      } else if (typeof fallback === 'string') {
        value = (await this.fliptClient.getStringValue(flagKey, fallback as unknown as string)) as unknown as T;
        this.recordEvaluationMetric({
          flagKey,
          namespace: this.fliptNamespace,
          environment: 'default',
          flagType: 'variant',
          match: value !== fallback,
          reason: 'MATCH_EVALUATION_REASON',
          segmentKeys: [],
          value: String(value),
        });
      } else if (typeof fallback === 'number') {
        value = (await this.fliptClient.getNumberValue(flagKey, fallback as unknown as number)) as unknown as T;
        this.recordEvaluationMetric({
          flagKey,
          namespace: this.fliptNamespace,
          environment: 'default',
          flagType: 'variant',
          match: value !== fallback,
          reason: 'MATCH_EVALUATION_REASON',
          segmentKeys: [],
          value: String(value),
        });
      } else {
        // For variant/JSON flags (e.g. dmb-screen-content), evaluate directly via in-memory
        // Wasm client with store context so targeted segment rules and default variants resolve synchronously.
        const rawClient = (this.fliptProvider as any)?._client;
        const siteNumber = this.currentSiteNumber || localStorage.getItem('storeId') || 'unknown';
        let val: any = fallback;

        if (rawClient) {
          const rawResult = rawClient.evaluateVariant({
            flagKey,
            entityId: `store-${siteNumber}`,
            context: { siteNumber },
          });

          match = Boolean(rawResult?.match ?? rawResult?.variantKey);
          reason = rawResult?.reason || 'MATCH_EVALUATION_REASON';
          segmentKeys = (rawResult?.segmentKeys || []).slice(0, 10).map((k: string) => k.slice(0, 64));
          variantValueStr = rawResult?.variantKey || 'default';

          if (rawResult?.variantAttachment) {
            try {
              val =
                typeof rawResult.variantAttachment === 'string'
                  ? JSON.parse(rawResult.variantAttachment)
                  : rawResult.variantAttachment;
            } catch {
              val = rawResult.variantAttachment;
            }
          }
        }

        if ((val === fallback || (Array.isArray(val) && val.length === 0)) && this.fliptClient) {
          const details = await this.fliptClient.getObjectDetails(flagKey, fallback as any);
          if (details?.value && !(Array.isArray(details.value) && details.value.length === 0)) {
            val = details.value;
          }
        }

        if (Array.isArray(fallback)) {
          if (!Array.isArray(val)) {
            if (val && typeof val === 'object') {
              if (Array.isArray(val.campaigns)) {
                val = val.campaigns;
              } else if (Array.isArray(val.data)) {
                val = val.data;
              } else if (Array.isArray(val.items)) {
                val = val.items;
              } else {
                val = fallback;
              }
            } else {
              val = fallback;
            }
          }
        }

        value = val as unknown as T;

        this.recordEvaluationMetric({
          flagKey,
          namespace: this.fliptNamespace,
          environment: 'default',
          flagType: 'variant',
          match,
          reason,
          segmentKeys,
          value: variantValueStr || (typeof value === 'object' && value ? (value as any).class || 'custom' : 'default'),
        });
      }

      this.logger.writeLog(`[Flipt] Evaluated ${flagKey}: ${JSON.stringify(value)}`, {
        flagKey,
        response: value,
        severity: level,
      });

      return value;
    } catch (error) {
      this.logger.writeLog(`[Flipt] Error evaluating ${flagKey}, using fallback`, {
        flagKey,
        error,
        severity: LogSeverity.WARNING,
      });
      return fallback;
    }
  }

  /* ----------------------------------------------------------------------- */
  /*  Flipt Metrics Bridge Ingest (Batched Client-Side Analytics)           */
  /* ----------------------------------------------------------------------- */

  private getMetricsBridgeUrl(): string | null {
    if (environment.name === 'local' || !this.fliptUrl || this.fliptUrl.includes('localhost')) {
      return null;
    }
    const envMatch = this.fliptUrl.match(/app\.(dev|tst|stg|prd)\.wendys\.digital/);
    const envSub = envMatch
      ? envMatch[1]
      : environment.name === 'test'
      ? 'tst'
      : environment.name === 'staging'
      ? 'stg'
      : environment.name === 'prod'
      ? 'prd'
      : 'dev';
    return `https://flipt-metrics-bridge.app.${envSub}.wendys.digital/ingest`;
  }

  private recordEvaluationMetric(entry: EvaluationMetricKey): void {
    const hash = `${entry.flagKey}|${entry.namespace}|${entry.environment}|${entry.flagType}|${entry.match}|${
      entry.reason
    }|${entry.segmentKeys.join(',')}|${entry.value}`;
    const existing = this.metricsBuffer.get(hash);
    if (existing) {
      existing.count += 1;
    } else {
      this.metricsBuffer.set(hash, { ...entry, count: 1 });
    }
  }

  private flushEvaluationMetrics(): void {
    if (this.metricsBuffer.size === 0) return;
    const url = this.getMetricsBridgeUrl();
    if (!url) {
      this.metricsBuffer.clear();
      return;
    }

    const items: EvaluationMetricItem[] = [];
    for (const item of this.metricsBuffer.values()) {
      // If a counter goes over 10,000, split it into several items per specification
      let remaining = item.count;
      while (remaining > 10000) {
        items.push({ ...item, count: 10000 });
        remaining -= 10000;
      }
      if (remaining > 0) {
        items.push({ ...item, count: remaining });
      }
    }
    this.metricsBuffer.clear();

    // If more than 500 items, split them into several requests per specification
    const CHUNK_SIZE = 500;
    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
      const chunk = items.slice(i, i + CHUNK_SIZE);
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(chunk),
        keepalive: true,
      }).catch(() => {
        // Best-effort fire-and-forget
      });
    }
  }

  private stream<T>(flagKey: string, fallback: T, level: LogSeverity): BehaviorSubject<T> {
    let cached = this.flagStreams.get(flagKey) as FlagStream<T> | undefined;
    if (cached) return cached.subject;

    const subj = new BehaviorSubject<T>(fallback);
    this.flagStreams.set(flagKey, { key: flagKey, subject: subj, fallback, logLevel: level });

    this.variation<T>(flagKey, fallback, level).then((v) => {
      if (!this.deepEqual(subj.getValue(), v)) {
        this.zone.run(() => subj.next(v));
      }
    });

    return subj;
  }

  /* =======================================================================
   *  PUBLIC FLAG GETTERS (100% Backwards-Compatible API)
   * ===================================================================== */

  private boolFlag(key: string, fallback = false, level = LogSeverity.INFO): Promise<boolean> {
    return this.variation<boolean>(key, fallback, level);
  }

  isRecordConversationEnabled(): Promise<boolean> {
    return this.boolFlag('a99ef0fb-33ad-4f6a-87fb-f33e2d1b7136', false);
  }

  async getCurrentTheme(): Promise<LDThemeCampaign[]> {
    return this.variation<LDThemeCampaign[]>('freshai-ui-theme', [], LogSeverity.DEBUG);
  }

  /* =======================================================================
   *  REACTIVE OBSERVABLE STREAMS
   * ===================================================================== */

  isRecordConversationEnabled$(): Observable<boolean> {
    return this.stream<boolean>('a99ef0fb-33ad-4f6a-87fb-f33e2d1b7136', false, LogSeverity.INFO);
  }

  currentThemeCampaigns$(): Observable<LDThemeCampaign[]> {
    return this.stream<LDThemeCampaign[]>('freshai-ui-theme', [], LogSeverity.DEBUG);
  }

  currentMenuContent(): Observable<Campaign[]> {
    return this.stream<any>('dmb-screen-content', [], LogSeverity.DEBUG);
  }

  currentMenuContent$(): Observable<Campaign[]> {
    return this.currentMenuContent();
  }

  currentMenuItemAlerts(): Observable<MenuItemAlert[]> {
    return this.stream<any>('fresh-ai-item-alerts', [], LogSeverity.DEBUG);
  }

  currentMenuItemAlerts$(): Observable<MenuItemAlert[]> {
    return this.currentMenuItemAlerts();
  }

  isStreamingAudioEnabled(): Observable<boolean> {
    return this.stream<any>('fresh-ai-agent-audio-streaming', false, LogSeverity.DEBUG);
  }

  isStreamingAudioEnabled$(): Observable<boolean> {
    return this.isStreamingAudioEnabled();
  }

  /* =======================================================================
   *  HELPER FUNCTIONS
   * ===================================================================== */

  getCampaignString(_isSimplifiedMenu?: boolean, _isOpsTestColdBrew?: boolean, _isMenuReinvention?: boolean): string {
    return CAMPAIGN_ID.national;
  }

  getCampaignTypes(): Promise<[boolean, boolean, boolean, { menu: string; endTime: Moment; success: boolean }]> {
    return this.locationService.storeCurrentTime().then((loc) => [false, false, false, loc]);
  }
}

/**
 * Backward-compatible alias for legacy injections
 */
@Injectable({
  providedIn: 'root',
  useExisting: LaunchDarklyService,
})
export class LaunchDarklyServiceExternal extends LaunchDarklyService {}
